import React from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useColorScheme,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useLocation } from 'react-router-dom';
import { activeItem } from './navigation';
import { useAuth } from '../../context/AuthContext';
import { Brand } from '../ui';
import { API_BASE } from '../../config/api';
import { layout } from '../../theme';

interface TopbarProps {
  mobile: boolean;
  sidebarWidth: number;
  onOpenSidebar: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  Admin: 'Administrator',
  UserManager: 'User Manager',
};

/** `api-staging.ractest.shop` reads clearer than the full URL in the chrome. */
const apiHost = (() => {
  try {
    return new URL(API_BASE).host;
  } catch {
    return API_BASE;
  }
})();

export const Topbar: React.FC<TopbarProps> = ({ mobile, sidebarWidth, onOpenSidebar }) => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { mode, systemMode, setMode } = useColorScheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const current = activeItem(location.pathname, user?.groups ?? []);
  const role = (user?.groups ?? []).map((g) => ROLE_LABELS[g]).find(Boolean) ?? 'Member';
  const initial = (user?.email ?? '?').charAt(0).toUpperCase();
  // `mode` is 'system' until the user picks one, so resolve through systemMode
  // to label the toggle by what is actually on screen.
  const isDark = (mode === 'system' ? systemMode : mode) === 'dark';

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { xs: '100%', md: `calc(100% - ${sidebarWidth}px)` },
        ml: { xs: 0, md: `${sidebarWidth}px` },
        transition: (theme) =>
          theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
      }}
    >
      <Toolbar sx={{ minHeight: layout.topbarHeight, gap: 1, px: { xs: 1.5, sm: 2.5 } }}>
        {mobile && (
          <IconButton edge="start" onClick={onOpenSidebar} aria-label="Open navigation">
            <MenuRoundedIcon />
          </IconButton>
        )}

        {mobile ? (
          <Brand size={30} subtitle="" />
        ) : (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
              {current?.label ?? 'AgentX Manager'}
            </Typography>
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={`API endpoint: ${API_BASE}`}>
          <Chip
            size="small"
            variant="outlined"
            label={apiHost}
            className="mono"
            sx={{ display: { xs: 'none', lg: 'inline-flex' }, color: 'text.secondary' }}
          />
        </Tooltip>

        <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton
            onClick={() => setMode(isDark ? 'light' : 'dark')}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.75 }} />

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ maxWidth: 260, minWidth: 0 }}
        >
          <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {user?.email ?? '—'}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
              {role}
            </Typography>
          </Box>
          <IconButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{ p: 0.25 }}
            aria-label="Account menu"
            aria-haspopup="menu"
          >
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              {initial}
            </Avatar>
          </IconButton>
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 230, mt: 1 } } }}
        >
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {user?.email ?? '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {role}
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          {/* Sign out is destructive-adjacent, so it is separated and tinted. */}
          <MenuItem onClick={signOut} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <LogoutRoundedIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Topbar;
